import { Formik } from "formik";
import * as yup from "yup";
import { Button } from "@repo/ui/components/Base/Button";
import type { TeamMember } from "@repo/types";
import { Input } from "@repo/ui/components/Base/Input";
import { Label } from "@repo/ui/components/Base/Label";
import { capitalize } from "@repo/shared/utils";
import { CLASSES, CLASSES_LABELS, ROLES } from "@/lib/constants";
import { useBlockUnload } from "@repo/ui/components/Base/hooks/useBlockUnload";
import { DatePicker } from "@repo/ui/components/Base/DatePicker";
import { CheckboxInput } from "@repo/ui/components/Base/CheckboxInput";
import { CheckboxGroupInput } from "@repo/ui/components/Base/CheckboxGroupInput";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { RoleDefinitionsDialog } from "@/components/common/RoleDefinitionsDialog";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import { MemberClassDefinitionsDialog } from "@/components/common/MemberClassDefinitionsDialog";
import { useUserList } from "@/hooks/queryHooks/userHooks";
import { teamRepo } from "@/repositories/teamRepo";
import { teamMemberRepo } from "@/repositories/teamMemberRepo";

export interface TeamMemberFormProps {
  tenantId: string;
  teamId: string;
  handleCancel: () => void;
  handleComplete: () => void;
  initialValues?: TeamMember;
  submitButtonText?: string;
  withinDialog?: boolean;
}

export const TeamMemberForm = ({
  tenantId,
  teamId,
  handleCancel,
  handleComplete,
  initialValues = teamMemberRepo.init(),
  submitButtonText = "Add",
  withinDialog,
}: TeamMemberFormProps) => {
  useBlockUnload();

  const { data: usersData } = useUserList(tenantId);
  const validationSchema = yup.object({
    userId: yup.string().required("User is required"),
    role: yup.string().required("Role is required"),
    class: yup.string().required("Class is required"),
  });

  return (
    <Formik
      initialValues={{
        ...initialValues,
      }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        if (!tenantId) {
          console.log("No tenant ID", tenantId);
          return;
        }

        await teamRepo.addUpdateMember(tenantId, teamId, { ...values });

        handleComplete();
      }}
    >
      {({
        values,
        handleChange,
        errors,
        touched,
        handleBlur,
        handleSubmit,
        setFieldValue,
        resetForm,
        isSubmitting,
      }) => {
        const footer = (
          <>
            <Button
              variant="destructive"
              type="button"
              disabled={isSubmitting}
              onClick={() => {
                resetForm();
                handleCancel();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {submitButtonText}
            </Button>
          </>
        );

        const form = (
          <div className="flex flex-col gap-y-4">
            <Label label="Team Member" />
            <SelectInput
              disabled={isSubmitting}
              onValueChange={(value) => {
                const userSelected = usersData?.find((u) => u.id === value);
                if (userSelected) {
                  setFieldValue("userId", value);
                }
              }}
              options={
                usersData?.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    <div>
                      {`${user.firstName} ${user.lastName} `}
                      <span className="ml-2 text-xs text-right">
                        {user.email}
                      </span>
                    </div>
                  </SelectItem>
                )) ?? []
              }
              value={values.userId || ""}
              placeholder="Select a User"
              error={!!errors.userId && touched.userId}
              errorMessage={errors.userId}
            />

            <Input
              id="title"
              label="Title"
              name="title"
              disabled={isSubmitting}
              value={values.title}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.title && touched.title}
              errorMessage={errors.title}
            />

            <div className="flex flex-row gap-x-2">
              <SelectInput
                label="*Role"
                disabled={isSubmitting}
                containerStyle="flex-grow"
                onValueChange={(value) => setFieldValue("role", value)}
                value={values.role || ""}
                options={ROLES.map((role) => (
                  <SelectItem key={role} value={role}>
                    {capitalize(role)}
                  </SelectItem>
                ))}
                placeholder="Select a user role"
                error={!!errors.role && touched.role}
                errorMessage={errors.role}
              />
              <RoleDefinitionsDialog />
            </div>

            <div className="flex flex-row gap-x-2">
              <SelectInput
                containerStyle="grow"
                label="*Class"
                disabled={isSubmitting}
                onValueChange={(value) => setFieldValue("class", value)}
                value={values.class || ""}
                options={CLASSES.map((cls) => (
                  <SelectItem key={cls} value={cls}>
                    {CLASSES_LABELS[cls]}
                  </SelectItem>
                ))}
                placeholder="Select a class for this user"
                error={!!errors.class}
                errorMessage={errors.class}
              />
              <MemberClassDefinitionsDialog />
            </div>

            <CheckboxInput
              id="meetingNotification"
              label="Get notified when meetings are published"
              disabled={isSubmitting}
              orientation="horizontal"
              checked={values.notifications?.meetingPublished ?? true}
              onCheckedChange={(value) => {
                setFieldValue("notifications.meetingPublished", value);
              }}
            />

            <CheckboxInput
              id="expires"
              label="This team member's term expires"
              disabled={isSubmitting}
              orientation="horizontal"
              checked={values.expires}
              onCheckedChange={(value) => {
                setFieldValue("expires", value);
              }}
            />
            {values.expires && (
              <DatePicker
                id="expirationDate"
                label="Expiration date"
                disabled={isSubmitting}
                date={values.expirationDate ?? new Date()}
                onDateChange={(value) => {
                  setFieldValue("expirationDate", value);
                }}
                calendarProps={{
                  fromDate: new Date(new Date().toDateString()),
                }}
              />
            )}
            {values.expires && (
              <CheckboxGroupInput
                disabled={isSubmitting}
                value={values.expirationNotification ?? []}
                onValueChange={(value) => {
                  setFieldValue("expirationNotification", value);
                }}
                options={[
                  {
                    key: "15",
                    label: "Notify 15 days before",
                  },
                  {
                    key: "30",
                    label: "Notify 30 days before",
                  },
                  {
                    key: "60",
                    label: "Notify 60 days before",
                  },
                  {
                    key: "90",
                    label: "Notify 90 days before",
                  },
                ]}
              />
            )}

            {!withinDialog && (
              <div className="flex flex-row justify-between gap-4">
                {footer}
              </div>
            )}
          </div>
        );

        return (
          <form onSubmit={handleSubmit}>
            {withinDialog ? (
              <>
                <DialogBody>{form}</DialogBody>
                <DialogFooter className="justify-between">
                  {footer}
                </DialogFooter>
              </>
            ) : (
              form
            )}
          </form>
        );
      }}
    </Formik>
  );
};
